// src/utils/videoPipeline.ts
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';

let ffmpegInstance: FFmpeg | null = null;
let ffmpegLoading: Promise<FFmpeg> | null = null;

async function getFFmpeg(): Promise<FFmpeg> {
  if (ffmpegInstance) return ffmpegInstance;

  ffmpegInstance = new FFmpeg();

  // Explicit core URLs so CRA/Vite can load WASM + worker reliably
  const base = 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.10/dist/umd';
  if (!ffmpegLoading) {
    ffmpegLoading = ffmpegInstance
      .load({
        coreURL: `${base}/ffmpeg-core.js`,
        wasmURL: `${base}/ffmpeg-core.wasm`,
        workerURL: `${base}/ffmpeg-core.worker.js`,
      })
      .then(() => ffmpegInstance!);
  }
  return ffmpegLoading;
}

function toArrayBuffer(u8: Uint8Array): ArrayBuffer {
  const ab = new ArrayBuffer(u8.byteLength);
  new Uint8Array(ab).set(u8); // copy bytes
  return ab;
}


/**
 * Ensure output is MP4 (H.264/AAC).
 * - First tries remux (-c copy) — fast & lossless when codecs already OK.
 * - Falls back to transcode only if remux fails.
 */
export async function ensureMp4H264Aac(file: File): Promise<File> {
  const ff = await getFFmpeg();

  const inExt = (file.name.split('.').pop() || 'mov').toLowerCase();
  const inName = `input.${inExt}`;
  const outName = 'out.mp4';

  await ff.writeFile(inName, await fetchFile(file));

  const readOut = async () => {
    const raw = (await ff.readFile(outName)) as unknown as Uint8Array;
    const buf: ArrayBuffer = toArrayBuffer(raw);
    return new File([buf], replaceExt(file.name, '.mp4'), { type: 'video/mp4' });
  };

  // 1) Try fast remux (no quality loss)
  try {
    await ff.exec(['-i', inName, '-c', 'copy', '-movflags', '+faststart', outName]);
    const out = await readOut();
    await safeDelete(ff, inName, outName);
    return out;
  } catch {
    // fall through to transcode
  }

  // 2) Transcode only if remux failed
  await ff.exec([
    '-i', inName,
    '-c:v', 'libx264', '-preset', 'veryfast', '-crf', '23', '-pix_fmt', 'yuv420p',
    '-c:a', 'aac', '-b:a', '128k',
    '-movflags', '+faststart',
    outName,
  ]);

  const out = await readOut();
  await safeDelete(ff, inName, outName);
  return out;
}

/* helpers */
function replaceExt(name: string, ext: string) {
  return name.replace(/\.[^.]+$/, '') + ext;
}

async function safeDelete(ff: FFmpeg, ...files: string[]) {
  for (const f of files) {
    try { await ff.deleteFile(f); } catch {}
  }
}
